<template>
  <v-dialog v-model="dialog" persistent max-width="900">
    <v-card class="form-container">
      <!-- Header with progress indicator -->
      <v-card-title class="form-header">
        <div class="d-flex align-center">
          <v-icon large color="#fff" class="mr-3">mdi-hand-heart</v-icon>
          <div>
            <h2 class="headline">Create New Volunteer Opportunity</h2>
            <v-progress-linear
              :value="formProgress"
              height="6"
              color="#fff"
              class="mt-2"
            ></v-progress-linear>
          </div>
        </div>
        <v-btn icon @click="onClose" class="close-btn">
          <v-icon color="#fff">mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <!-- Form content with animated sections -->
      <v-card-text class="form-content">
        <v-alert
          dense
          type="error"
          v-if="error"
          class="mx-4 mb-4"
          dismissible
          @input="error = null"
        >
          {{ error }}
        </v-alert>
        <v-stepper v-model="currentStep" vertical non-linear>
          <!-- Basic Information Step -->
          <v-stepper-step :complete="currentStep > 1" step="1" :editable="true">
            <div class="step-header">
              <v-icon color="primary" class="mr-2">mdi-text-box-outline</v-icon>
              <span>Basic Information</span>
            </div>
          </v-stepper-step>
          <v-stepper-content step="1">
            <v-container fluid class="pa-0">
              <v-row>
                <v-col cols="12" md="6">
                  <v-select
                    v-model="need.status"
                    :items="statusOptions"
                    label="Status*"
                    outlined
                    dense
                    prepend-icon="mdi-status"
                    :error-messages="statusMessage"
                    @blur="validateField('status')"
                    @input="onChange"
                    class="pt-4"
                  ></v-select>
                </v-col>
                <v-col cols="12" md="6">
                  <v-select
                    v-model="need.privacy"
                    :items="privacyItems"
                    label="Privacy Level*"
                    outlined
                    dense
                    prepend-icon="mdi-lock-outline"
                    :error-messages="privacyMessage"
                    @blur="validateField('privacy')"
                    @input="onChange"
                    class="pt-4"
                  ></v-select>
                </v-col>
              </v-row>

              <v-text-field
                v-model="need.title"
                label="Opportunity Title*"
                outlined
                dense
                prepend-icon="mdi-format-title"
                :error-messages="titleMessage"
                @blur="validateField('title')"
                @input="onChange"
                class="mb-4"
              ></v-text-field>

              <v-textarea
                v-model="need.description"
                label="Description"
                outlined
                auto-grow
                rows="3"
                prepend-icon="mdi-text"
                hint="What will volunteers be doing? What should they bring?"
                persistent-hint
                class="mb-4"
              ></v-textarea>

              <div class="text-right">
                <v-btn
                  color="primary"
                  @click="validateStep(1)"
                  class="next-btn"
                  :disabled="!isStep1Valid"
                >
                  Next: Timing
                  <v-icon right>mdi-arrow-right</v-icon>
                </v-btn>
              </div>
            </v-container>
          </v-stepper-content>

          <!-- Timing Step -->
          <v-stepper-step
            :complete="currentStep > 2"
            step="2"
            :editable="currentStep > 2 || isStep1Valid"
            :disabled="!isStep1Valid"
          >
            <div class="step-header">
              <v-icon color="primary" class="mr-2">mdi-clock-outline</v-icon>
              <span>Duration</span>
            </div>
          </v-stepper-step>
          <v-stepper-content step="2">
            <v-container fluid class="pa-0">
              <v-row>
                <v-col cols="12">
                  <v-select
                    v-model="need.duration"
                    :items="durationItems"
                    label="Duration Type*"
                    outlined
                    dense
                    prepend-icon="mdi-calendar"
                    :error-messages="durationMessage"
                    @input="onChange"
                    @blur="validateField('duration')"
                    class="pt-4"
                  ></v-select>
                </v-col>
              </v-row>

              <!-- Dynamic form components based on duration selection -->
              <transition name="slide-fade" mode="out-in">
                <agency-is-ongoing-input-form
                  :need="need"
                  v-if="need.duration == 'Is Ongoing'"
                  :startDateErrorMessage="start_dateErrorMessage"
                  :endDateErrorMessage="end_dateErrorMessage"
                  :onChange="onChange"
                  ref="sub"
                />
                <agency-runs-until-input-form
                  :need="need"
                  v-else-if="need.duration == 'Runs Until'"
                  :hoursMessage="hoursMessage"
                  :capacityMessage="capacityMessage"
                  :startDateErrorMessage="start_dateErrorMessage"
                  :endDateErrorMessage="end_dateErrorMessage"
                  :hoursDescriptionMessage="hoursDescriptionMessage"
                  :onChange="onChange"
                  ref="sub"
                />
                <agency-happens-on-input-form
                  :need="need"
                  v-else-if="need.duration == 'Happens On'"
                  :hoursMessage="hoursMessage"
                  :capacityMessage="capacityMessage"
                  :startDateErrorMessage="start_dateErrorMessage"
                  :hoursDescriptionMessage="hoursDescriptionMessage"
                  :onChange="onChange"
                  ref="sub"
                />
                <agency-multi-date-input-form
                  :need="need"
                  v-else-if="need.duration == 'Multi-Date'"
                  :hoursMessage="hoursMessage"
                  :capacityMessage="capacityMessage"
                  :startDateErrorMessage="start_dateErrorMessage"
                  :endDateErrorMessage="end_dateErrorMessage"
                  :hoursDescriptionMessage="hoursDescriptionMessage"
                  :onChange="onChange"
                  ref="sub"
                />
                <agency-custom-shifts-input-form
                  :need="need"
                  v-else-if="need.duration == 'Custom Shifts'"
                  :hoursMessage="hoursMessage"
                  :capacityMessage="capacityMessage"
                  :startDateErrorMessage="start_dateErrorMessage"
                  :endDateErrorMessage="end_dateErrorMessage"
                  ref="sub"
                />
                <agency-recurring-shifts-input-form
                  :need="need"
                  v-else-if="need.duration == 'Recurring Shifts'"
                  :hoursMessage="hoursMessage"
                  :capacityMessage="capacityMessage"
                  :startDateErrorMessage="start_dateErrorMessage"
                  :endDateErrorMessage="end_dateErrorMessage"
                  :occursMessage="occursMessage"
                  :repeatMessage="repeatMessage"
                  :repeatOnMessage="repeatOnMessage"
                  :selectDayMessage="selectDayMessage"
                  :occurrenceMessage="occurrenceMessage"
                  :hoursDescriptionMessage="hoursDescriptionMessage"
                  :onChange="onChange"
                  ref="sub"
                />
              </transition>

              <div class="d-flex justify-space-between mt-4">
                <v-btn text @click="currentStep = 1" class="back-btn">
                  <v-icon left>mdi-arrow-left</v-icon>
                  Back
                </v-btn>
                <v-btn
                  color="primary"
                  @click="validateStep(2)"
                  class="next-btn"
                  :disabled="!isStep2Valid"
                >
                  Next: Volunteer Details
                  <v-icon right>mdi-arrow-right</v-icon>
                </v-btn>
              </div>
            </v-container>
          </v-stepper-content>

          <!-- Volunteer Details Step -->
          <v-stepper-step
            :complete="currentStep > 3"
            step="3"
            :editable="currentStep > 3 || (isStep1Valid && isStep2Valid)"
            :disabled="!(isStep1Valid && isStep2Valid)"
          >
            <div class="step-header">
              <v-icon color="primary" class="mr-2">mdi-account-group</v-icon>
              <span>Volunteer Details</span>
            </div>
          </v-stepper-step>
          <v-stepper-content step="3">
            <v-container fluid class="pa-0">
              <v-row>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model.number="need.min_age"
                    label="Minimum Age*"
                    outlined
                    dense
                    type="number"
                    prepend-icon="mdi-numeric"
                    :error-messages="minAgeMessage"
                    @input="validateAgeFields"
                    @blur="validateField('min_age')"
                    :min="1"
                    :max="120"
                    :error="!!minAgeMessage"
                    class="pt-4"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model.number="need.max_age"
                    label="Maximum Age*"
                    outlined
                    dense
                    type="number"
                    prepend-icon="mdi-numeric"
                    :error-messages="maxAgeMessage"
                    @input="validateAgeFields"
                    @blur="validateField('max_age')"
                    :min="1"
                    :max="120"
                    :error="!!maxAgeMessage"
                    class="pt-4"
                  ></v-text-field>
                </v-col>
              </v-row>

              <v-row>
                <v-col cols="12" md="4">
                  <v-select
                    v-model="need.family_friendly"
                    :items="YesNoOptions"
                    label="Family Friendly?"
                    outlined
                    dense
                    prepend-icon="mdi-family-tree"
                  ></v-select>
                </v-col>
                <v-col cols="12" md="4">
                  <v-select
                    v-model="need.outdoors"
                    :items="YesNoOptions"
                    label="Outdoor Activity?"
                    outlined
                    dense
                    prepend-icon="mdi-weather-sunny"
                  ></v-select>
                </v-col>
                <v-col cols="12" md="4">
                  <v-select
                    v-model="need.wheelchair_accessible"
                    :items="YesNoOptions"
                    label="Wheelchair Accessible?"
                    outlined
                    dense
                    prepend-icon="mdi-wheelchair-accessibility"
                  ></v-select>
                </v-col>
              </v-row>

              <v-row>
                <v-col cols="12">
                  <v-combobox
                    v-model="need.attributes"
                    label="Special Attributes"
                    outlined
                    multiple
                    chips
                    deletable-chips
                    prepend-icon="mdi-tag-multiple"
                    hint="Highlight special features (e.g., 'Snacks provided', 'Wear closed-toe shoes')"
                    persistent-hint
                    hide-details
                    hide-no-data
                    :menu-props="{ closeOnContentClick: false }"
                    append-icon=""
                  ></v-combobox>
                </v-col>
              </v-row>

              <div class="d-flex justify-space-between mt-4">
                <v-btn text @click="currentStep = 2" class="back-btn">
                  <v-icon left>mdi-arrow-left</v-icon>
                  Back
                </v-btn>
                <v-btn
                  color="primary"
                  @click="validateStep(3)"
                  class="next-btn"
                  :disabled="!isStep3Valid"
                >
                  Next: Location & Interests
                  <v-icon right>mdi-arrow-right</v-icon>
                </v-btn>
              </div>
            </v-container>
          </v-stepper-content>

          <!-- Location & Interests Step -->
          <v-stepper-step
            :complete="currentStep > 4"
            step="4"
            :editable="
              currentStep > 4 || (isStep1Valid && isStep2Valid && isStep3Valid)
            "
            :disabled="!(isStep1Valid && isStep2Valid && isStep3Valid)"
          >
            <div class="step-header">
              <v-icon color="primary" class="mr-2">mdi-map-marker</v-icon>
              <span>Location & Interests</span>
            </div>
          </v-stepper-step>
          <v-stepper-content step="4">
            <v-container fluid class="pa-0">
              <!-- Map Address Selector -->
              <v-row>
                <v-col cols="12">
                  <v-card outlined class="mb-4">
                    <v-card-text class="pb-2">
                      <div class="d-flex align-center mb-3">
                        <v-icon color="primary" class="mr-2"
                          >mdi-map-search</v-icon
                        >
                        <span class="subtitle-1 font-weight-medium">
                          Use Map to Auto-Fill Address
                        </span>
                        <v-spacer></v-spacer>
                        <MapAddressDialog
                          @address-selected="onMapAddressSelected"
                        />
                      </div>
                      <div class="text--secondary text-caption">
                        Click "Select on Map" to search for an address or click
                        directly on the map to auto-fill all location fields.
                      </div>
                    </v-card-text>
                  </v-card>
                </v-col>
              </v-row>

              <!-- Address Input Section -->
              <v-row>
                <v-col cols="12">
                  <v-text-field
                    v-model="need.address"
                    label="Street Address*"
                    outlined
                    dense
                    prepend-icon="mdi-home-map-marker"
                    :error-messages="addressMessage"
                    @blur="validateField('address')"
                    @input="onChange"
                    class="pt-4"
                  ></v-text-field>
                </v-col>
              </v-row>

              <!-- Location Fields -->
              <v-row>
                <v-col cols="12" md="3">
                  <v-text-field
                    v-model="need.zip"
                    label="Zip Code*"
                    outlined
                    @blur="validateField('zip')"
                    @input="onZipInput"
                    dense
                    prepend-icon="mdi-mailbox"
                    :error-messages="zipMessage"
                    maxlength="10"
                    persistent-hint
                    :hint="
                      zipLoadingMessage ||
                      'Enter zip code to auto-fill city and state'
                    "
                    :loading="zipLoading"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="need.city"
                    label="City*"
                    outlined
                    dense
                    prepend-icon="mdi-city"
                    @blur="validateField('city')"
                    @input="onChange"
                    :error-messages="cityMessage"
                    :readonly="cityStateReadonly"
                    :hint="cityStateReadonly ? 'Auto-filled from zip code' : ''"
                    persistent-hint
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="3">
                  <v-select
                    v-model="need.state"
                    :items="stateOptions"
                    label="State*"
                    outlined
                    dense
                    prepend-icon="mdi-map-legend"
                    :error-messages="stateMessage"
                    @blur="validateField('state')"
                    @input="onChange"
                    :readonly="cityStateReadonly"
                    :hint="cityStateReadonly ? 'Auto-filled from zip code' : ''"
                    persistent-hint
                  ></v-select>
                </v-col>
              </v-row>

              <!-- Reset Address Button (when map was used) -->
              <v-row v-if="cityStateReadonly">
                <v-col cols="12">
                  <v-alert dense type="info" outlined class="mb-3">
                    <div class="d-flex align-center">
                      <div class="flex-grow-1">
                        <strong>Address auto-filled from map:</strong>
                        <br />
                        <small>{{
                          mapSelectedAddress?.formatted_address
                        }}</small>
                      </div>
                      <v-btn
                        small
                        text
                        color="primary"
                        @click="resetMapSelection"
                      >
                        <v-icon left small>mdi-refresh</v-icon>
                        Edit Manually
                      </v-btn>
                    </div>
                  </v-alert>
                </v-col>
              </v-row>

              <v-row>
                <v-col cols="12">
                  <v-menu
                    v-model="menu"
                    :close-on-content-click="false"
                    offset-y
                    max-height="400"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-select
                        :items="interests"
                        v-model="need.interests"
                        label="Related Interests*"
                        outlined
                        multiple
                        chips
                        @blur="validateField('interests')"
                        @input="onChange"
                        deletable-chips
                        prepend-icon="mdi-heart-multiple"
                        :error-messages="interestsMessage"
                        readonly
                        v-bind="attrs"
                        v-on="on"
                        :value="need.interests"
                      >
                        <template v-slot:selection="{ attrs, item, selected }">
                          <v-chip
                            v-bind="attrs"
                            :color="`${item.color} lighten-3`"
                            :input-value="selected"
                            small
                            class="interest-chip"
                            close
                            @click:close="removeInterest(item)"
                          >
                            <span class="pr-2">{{ item }}</span>
                            <v-icon
                              small
                              @click.stop="onSelectPrimaryInterest(item)"
                            >
                              {{
                                item == need.primaryInterest
                                  ? "mdi-star"
                                  : "mdi-star-outline"
                              }}
                            </v-icon>
                          </v-chip>
                        </template>
                      </v-select>
                    </template>

                    <v-card>
                      <v-list dense>
                        <v-subheader>Select multiple interests</v-subheader>
                        <v-list-item
                          v-for="(item, index) in interests"
                          :key="index"
                          dense
                        >
                          <v-list-item-action class="my-0 mr-2">
                            <v-checkbox
                              :input-value="need.interests.includes(item)"
                              :value="item"
                              hide-details
                              @change="() => toggleInterest(item)"
                              @click.stop
                              :ripple="false"
                              :indeterminate="false"
                              :true-value="true"
                              :false-value="false"
                              :checked="need.interests.includes(item)"
                            ></v-checkbox>
                          </v-list-item-action>
                          <v-list-item-content @click="toggleInterest(item)">
                            <v-list-item-title>{{ item }}</v-list-item-title>
                          </v-list-item-content>
                          <v-list-item-action class="my-0 ml-2">
                            <v-icon
                              small
                              @click.stop="onSelectPrimaryInterest(item)"
                              :color="
                                item == need.primaryInterest ? 'amber' : ''
                              "
                            >
                              {{
                                item == need.primaryInterest
                                  ? "mdi-star"
                                  : "mdi-star-outline"
                              }}
                            </v-icon>
                          </v-list-item-action>
                        </v-list-item>
                      </v-list>
                      <v-divider></v-divider>
                      <v-card-actions class="px-4 py-3">
                        <v-btn text @click="menu = false">Cancel</v-btn>
                        <v-spacer></v-spacer>
                        <v-btn
                          color="primary"
                          @click="menu = false"
                          :disabled="!(need.interests && need.interests.length)"
                        >
                          OK ({{ need.interests ? need.interests.length : 0 }}
                          selected)
                        </v-btn>
                      </v-card-actions>
                    </v-card>
                  </v-menu>
                  <div class="interest-hint">
                    <v-icon small color="primary">mdi-information</v-icon>
                    <span
                      >First selected interest will be primary (starred)</span
                    >
                  </div>
                </v-col>
              </v-row>

              <v-row>
                <v-col cols="12">
                  <need-photo-viewer
                    :editable="true"
                    :image-data="need.images"
                    :on-updated-image="onUpdatedImage"
                  />
                </v-col>
              </v-row>

              <div class="d-flex justify-space-between mt-4">
                <v-btn text @click="currentStep = 3" class="back-btn">
                  <v-icon left>mdi-arrow-left</v-icon>
                  Back
                </v-btn>
                <v-btn
                  color="primary"
                  @click="validateStep(4)"
                  class="next-btn"
                  :disabled="!isStep4Valid"
                >
                  Next: Additional Details
                  <v-icon right>mdi-arrow-right</v-icon>
                </v-btn>
              </div>
            </v-container>
          </v-stepper-content>

          <!-- Final Details Step -->
          <v-stepper-step
            step="5"
            :editable="
              isStep1Valid && isStep2Valid && isStep3Valid && isStep4Valid
            "
            :disabled="
              !(isStep1Valid && isStep2Valid && isStep3Valid && isStep4Valid)
            "
          >
            <div class="step-header">
              <v-icon color="primary" class="mr-2"
                >mdi mdi-information-slab-circle-outline</v-icon
              >
              <span>Additional Details</span>
            </div>
          </v-stepper-step>
          <v-stepper-content step="5">
            <v-container fluid class="pa-0">
              <v-row>
                <v-col cols="12" md="6">
                  <v-select
                    v-model="need.virtual_need"
                    :items="options"
                    label="Virtual Opportunity?"
                    outlined
                    dense
                    prepend-icon="mdi-monitor"
                    class="pt-4"
                  ></v-select>
                </v-col>
                <v-col cols="12" md="6">
                  <v-select
                    v-model="need.cori_report"
                    :items="options"
                    label="Require CORI Report?"
                    outlined
                    dense
                    prepend-icon="mdi-shield-account"
                    class="pt-4"
                  ></v-select>
                </v-col>
              </v-row>

              <v-alert dense type="error" v-if="error" class="mx-4">
                {{ error }}
              </v-alert>

              <div class="d-flex justify-space-between mt-4">
                <v-btn text @click="currentStep = 4" class="back-btn">
                  <v-icon left>mdi-arrow-left</v-icon>
                  Back
                </v-btn>
                <v-btn
                  color="success"
                  @click="onSave"
                  :loading="loading"
                  class="submit-btn"
                >
                  <v-icon left>mdi-check</v-icon>
                  Create Opportunity
                </v-btn>
              </div>
            </v-container>
          </v-stepper-content>
        </v-stepper>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import DOMPurify from "dompurify";
import AgencyIsOngoingInputForm from "./AgencyIsOngoingInputForm.vue";
import AgencyRunsUntilInputForm from "./AgencyRunsUntilInputForm.vue";
import AgencyHappensOnInputForm from "./AgencyHappensOnInputForm.vue";
import AgencyMultiDateInputForm from "./AgencyMultiDateInputForm.vue";
import AgencyCustomShiftsInputForm from "./AgencyCustomShiftsInputForm.vue";
import AgencyRecurringShiftsInputForm from "./AgencyRecurringShiftsInputForm.vue";
import { mapActions, mapState } from "vuex";
import NeedPhotoViewer from "../../../../components/agency/NeedPhotoViewer.vue";
import MapAddressDialog from "./MapAddressDialog.vue";

export default {
  components: {
    AgencyIsOngoingInputForm,
    AgencyRunsUntilInputForm,
    AgencyHappensOnInputForm,
    AgencyMultiDateInputForm,
    AgencyCustomShiftsInputForm,
    AgencyRecurringShiftsInputForm,
    NeedPhotoViewer,
    MapAddressDialog,
  },
  props: {
    dialog: {
      type: Boolean,
      default: false,
    },
    onCloseDialog: {
      type: Function,
    },
  },
  data() {
    return {
      need: {
        repeat_on: [],
        images: [],
        interests: [],
        min_age: null,
        max_age: null,
        family_friendly: "No",
        outdoors: "No",
        wheelchair_accessible: "No",
        virtual_need: "No",
        cori_report: "No",
        auto_accept: "No",
        status: "Active",
        privacy: "Public",
        duration: "Is Ongoing",
        custom_shifts: [],
      },
      customShiftsMessage: null,
      error: null,
      menu: false,
      loading: false,
      privacyItems: ["Public", "Private"],
      durationItems: [
        "Runs Until",
        "Happens On",
        "Multi-Date",
        "Custom Shifts",
        "Recurring Shifts",
      ],
      allowTeamOptions: ["No", "Yes", "Teams only"],
      YesNoOptions: ["Yes", "No", "N/A"],
      options: ["Yes", "No"],
      stateOptions: [
        "AK",
        "AL",
        "AR",
        "AS",
        "AZ",
        "CA",
        "CO",
        "CT",
        "DC",
        "DE",
        "FL",
        "FM",
        "GA",
        "GU",
        "HI",
        "IA",
        "ID",
        "IL",
        "IN",
        "KS",
        "KY",
        "LA",
        "MA",
        "MD",
        "ME",
        "MH",
        "MI",
        "MN",
        "MO",
        "MP",
        "MS",
        "MT",
        "NC",
        "ND",
        "NE",
        "NH",
        "NJ",
        "NM",
        "NV",
        "NY",
        "OH",
        "OK",
        "OR",
        "PA",
        "PR",
        "PW",
        "RI",
        "SC",
        "SD",
        "TN",
        "TX",
        "UT",
        "VA",
        "VI",
        "VT",
        "WA",
        "WI",
        "WV",
        "WY",
      ],
      statusOptions: ["Active", "Pending", "Inactive"],
      snackbar: false,
      snackMessage: null,
      titleMessage: null,
      statusMessage: null,
      privacyMessage: null,
      minAgeMessage: null,
      maxAgeMessage: null,
      durationMessage: null,
      hoursMessage: null,
      hoursDescriptionMessage: null,
      occursMessage: null,
      repeatMessage: null,
      repeatOnMessage: null,
      selectDayMessage: null,
      capacityMessage: null,
      start_dateErrorMessage: null,
      end_dateErrorMessage: null,
      registrationDateErrorMessage: null,
      zipMessage: null,
      interestsMessage: null,
      addressMessage: null,
      cityMessage: null,
      cityStateReadonly: false,
      mapSelectedAddress: null,
      zipLoading: false,
      zipLoadingMessage: "",
      stateMessage: null,
      occurrenceMessage: null,
      registrationClosedDateMessage: null,
      currentStep: 1,
      validationRules: {
        title: (value) => !!value || "Title is required",
        status: (value) => !!value || "Status is required",
        privacy: (value) => !!value || "Privacy level is required",
        duration: (value) => !!value || "Duration type is required",
        min_age: (value) => {
          if (value === null || value === undefined || value === "")
            return "Minimum age is required";
          const num = Number(value);
          if (isNaN(num)) return "Must be a valid number";
          if (num < 14) return "Minimum age must be at least 14";
          if (num > 120) return "Maximum age is 120";
          return true;
        },
        max_age: (value, form) => {
          if (value === null || value === undefined || value === "")
            return "Maximum age is required";
          const num = Number(value);
          if (isNaN(num)) return "Must be a valid number";
          if (num < 14) return "Minimum age must be at least 14";
          if (num > 120) return "Maximum age is 120";
          if (form.min_age && num < Number(form.min_age)) {
            return "Must be greater than minimum age";
          }
          return true;
        },
        address: (value) => !!value || "Address is required",
        city: (value) => !!value || "City is required",
        state: (value) => !!value || "State is required",
        zip: (value) => {
          if (!value) return "Zip code is required";

          // Remove any spaces for validation
          const cleanValue = value.replace(/\s/g, "");

          // Check for basic format: 5 digits or 5 digits + dash + 4 digits
          if (!/^\d{5}(-\d{4})?$/.test(cleanValue)) {
            if (cleanValue.length < 5) {
              return "Zip code must be at least 5 digits";
            } else if (cleanValue.length > 5 && cleanValue.length < 10) {
              return "Extended zip code must be 9 digits (12345-6789)";
            } else {
              return "Invalid zip code format. Use 12345 or 12345-6789";
            }
          }

          return true;
        },
        interests: (value) =>
          value && value.length > 0
            ? true
            : "At least one interest is required",
        start_date: (value, form) => {
          if (form.duration === "Is Ongoing") return true;
          if (!value) return "Start date is required";
          return true;
        },
        end_date: (value, form) => {
          if (form.duration === "Is Ongoing") return true;
          if (!value) return "End date is required";
          if (form.start_date && new Date(value) < new Date(form.start_date)) {
            return "Must be after start date";
          }
          return true;
        },
          custom_shifts: (value, form) => {
    if (form.duration !== "Custom Shifts") return true;
    if (!value || !Array.isArray(value) || value.length === 0) {
      return "At least one custom shift is required";
    }
    return true;
  },
        hours: (value, form) => {
          if (
            form.duration === "Is Ongoing" ||
            form.duration === "Custom Shifts"
          )
            return true;
          if (!value) return "Hours are required";
          return true;
        },
        hours_description: (value, form) => {
          if (
            form.duration === "Is Ongoing" ||
            form.duration === "Custom Shifts"
          )
            return true;
          if (!value) return "Hours description is required";
          return true;
        },
        capacity: (value, form) => {
          if (form.duration === "Is Ongoing") return true;
          if (!value && value !== 0) return "Capacity is required";
          if (value < 1) return "Capacity must be at least 1";
          return true;
        },
        occurs: (value, form) => {
          if (form.duration !== "Recurring Shifts") return true;
          if (!value) return "Occurrence frequency is required";
          return true;
        },
        repeat_every: (value, form) => {
          if (form.duration !== "Recurring Shifts" || form.occurs !== "Daily")
            return true;
          if (!value) return "Repeat frequency is required";
          return true;
        },
        repeat_on: (value, form) => {
          if (form.duration !== "Recurring Shifts" || form.occurs !== "Weekly")
            return true;
          if (!value || !value.length) return "Repeat days are required";
          return true;
        },
        dow: (value, form) => {
          if (form.duration !== "Recurring Shifts" || form.occurs !== "Monthly")
            return true;
          if (!value) return "Day of week is required";
          return true;
        },
        occurrence: (value, form) => {
          if (form.duration !== "Recurring Shifts" || form.occurs !== "Monthly")
            return true;
          if (!value) return "Occurrence is required";
          return true;
        },
      },
    };
  },
  methods: {
    ...mapActions({
      createNeed: "need/addNeed",
      getInterestIcons: "icon/getInterestIcons",
    }),
    onClose() {
      this.need = {
        repeat_on: [],
        images: [],
        interests: [],
        min_age: null,
        max_age: null,
        family_friendly: "No",
        outdoors: "No",
        wheelchair_accessible: "No",
        virtual_need: "No",
        cori_report: "No",
        auto_accept: "No",
        status: "Active",
        privacy: "Public",
        duration: "Is Ongoing",
        custom_shifts: [], 
      };
      this.currentStep = 1;
      this.clearErrorMessages();
      this.onCloseDialog();
    },
    // Utility sanitizer
    sanitizeInput(value) {
      if (!value) return value;

      // Step 1: Sanitize unsafe HTML but allow text inside <script> to survive
      let clean = DOMPurify.sanitize(value, {
        ALLOWED_TAGS: [],
        ALLOWED_ATTR: [],
      });

      // Step 2: If DOMPurify returns empty string, fallback to extracting inner text
      if (!clean.trim()) {
        // Create a temporary div to extract text content
        const temp = document.createElement("div");
        temp.innerHTML = value;
        clean = temp.textContent || temp.innerText || "";
      }

      return clean.trim();
    },
      // Add method to handle custom shifts updates
  onCustomShiftsUpdate(shifts) {
    this.need.custom_shifts = shifts;
    this.customShiftsMessage = null; // Clear any existing error
    this.$forceUpdate(); // Force reactivity update
  },
    removeInterest(item) {
      const index = this.need.interests.indexOf(item);
      if (index !== -1) {
        this.need.interests.splice(index, 1);
        if (this.need.primaryInterest === item) {
          this.need.primaryInterest = this.need.interests[0] || null;
        }
      }
      this.validateField("interests");
    },
    toggleInterest(item) {
      const index = this.need.interests.indexOf(item);
      if (index === -1) {
        this.need.interests.push(item);
        if (!this.need.primaryInterest) {
          this.need.primaryInterest = item;
        }
      } else {
        this.need.interests.splice(index, 1);
        if (this.need.primaryInterest === item) {
          this.need.primaryInterest = this.need.interests[0] || null;
        }
      }
      this.validateField("interests");
    },
    onSelectPrimaryInterest(item) {
      if (this.need.interests.includes(item)) {
        this.need.primaryInterest = item;
      }
    },
    // Replace the onMapAddressSelected method around line 1040
    onMapAddressSelected(addressData) {
      // Add debugging to see what we're getting from the map

      // Improved address handling with multiple fallbacks
      let streetAddress = "";

      if (addressData.address && addressData.address.trim()) {
        streetAddress = addressData.address.trim();
      } else if (addressData.formatted_address) {
        // Extract street address from formatted address
        // Example: "123 Main St, Anytown, ST 12345, USA" -> "123 Main St"
        const parts = addressData.formatted_address.split(",");
        streetAddress = parts[0].trim();
      }

      // Auto-fill all location fields from map selection
      this.need.address = streetAddress;
      this.need.city = addressData.city || "";
      this.need.state = addressData.state || "";
      this.need.zip = addressData.zip || "";

      // Store the full address data for reference
      this.mapSelectedAddress = addressData;

      // Mark fields as readonly since they came from map
      this.cityStateReadonly = true;

      // Clear any existing validation errors
      this.clearLocationValidationErrors();

      // Trigger validation
      this.validateLocationFields();

      // Show success message
      this.$notify({
        type: "success",
        title: "Address Selected",
        text: `Address auto-filled from map: ${
          streetAddress || addressData.formatted_address
        }`,
      });

      // Trigger onChange to mark form as dirty
      this.onChange();

      // Force update to ensure reactivity
      this.$nextTick(() => {
        this.$forceUpdate();
      });
    },
    onZipInput(value) {
      // Format zip code as user types
      let formatted = value.replace(/[^\d-]/g, ""); // Remove non-digit and non-dash characters

      // Limit to 10 characters (12345-6789)
      if (formatted.length > 10) {
        formatted = formatted.substring(0, 10);
      }

      // Auto-format with dash for extended zip codes
      if (formatted.length === 6 && !formatted.includes("-")) {
        formatted = formatted.substring(0, 5) + "-" + formatted.substring(5);
      }

      // Update the model
      this.need.zip = formatted;

      // Reset readonly state when user manually edits zip
      if (this.cityStateReadonly) {
        this.cityStateReadonly = false;
        this.mapSelectedAddress = null;
      }

      // Call existing zip validation logic
      this.validateZipCodeAndAutoFill();
      this.onChange();
    },

    clearLocationValidationErrors() {
      this.addressMessage = null;
      this.cityMessage = null;
      this.stateMessage = null;
      this.zipMessage = null;
    },

    validateLocationFields() {
      this.validateField("address");
      this.validateField("city");
      this.validateField("state");
      this.validateField("zip");
    },

    resetMapSelection() {
      this.cityStateReadonly = false;
      this.mapSelectedAddress = null;
      this.zipLoadingMessage = "";

      // Clear fields to allow manual entry
      this.clearLocationValidationErrors();

      this.$notify({
        type: "info",
        title: "Manual Entry Enabled",
        text: "You can now edit the location fields manually",
      });
    },

    onUpdatedImage(images) {
      this.need.images = images;
      this.clearErrorMessages();
    },
    validateAgeFields() {
      this.validateField("min_age");
      this.validateField("max_age");

      // Additional validation for age range
      if (this.need.min_age !== null && this.need.max_age !== null) {
        if (Number(this.need.min_age) > Number(this.need.max_age)) {
          this.maxAgeMessage = "Must be greater than minimum age";
        } else if (this.maxAgeMessage === "Must be greater than minimum age") {
          this.maxAgeMessage = null;
        }
      }
      // Always check and set minAgeMessage for < 14
      if (this.need.min_age !== null && this.need.min_age !== "") {
        if (Number(this.need.min_age) < 14) {
          this.minAgeMessage = "Minimum age must be at least 14";
        } else if (this.minAgeMessage === "Minimum age must be at least 14") {
          this.minAgeMessage = null;
        }
      }

      if (this.need.max_age !== null && this.need.max_age !== "") {
        if (Number(this.need.max_age) > 100) {
          this.maxAgeMessage = "Maximum age must be at most 100";
        } else if (this.maxAgeMessage === "Maximum age must be at most 100") {
          this.maxAgeMessage = null;
        }
      }
    },
    validateField(field) {
      const rule = this.validationRules[field];
      if (typeof rule !== "function") {
        console.error(`Validation rule for ${field} is not a function`);
        return true;
      }

      const result = rule(this.need[field], this.need);
      if (result === true) {
        this[`${field}Message`] = null;
        return true;
      } else {
        this[`${field}Message`] = result;
        return false;
      }
    },
    async validateZipCodeAndAutoFill() {
      const zip = this.need.zip;

      // Reset state
      this.zipLoading = false;
      this.zipLoadingMessage = "";

      if (!zip || zip.length < 5) {
        this.zipMessage =
          zip.length > 0 && zip.length < 5 ? "Zip code must be 5 digits" : null;
        return;
      }

      if (!/^\d{5}$/.test(zip)) {
        this.zipMessage = "Zip code must contain only numbers";
        return;
      }

      this.zipLoading = true;
      this.zipLoadingMessage = "Validating zip code...";

      try {
        const response = await fetch(`https://api.zippopotam.us/us/${zip}`);

        if (response.ok) {
          const data = await response.json();
          const place = data.places[0];

          if (place) {
            // Only auto-fill if not from map selection
            if (!this.cityStateReadonly) {
              this.need.city = place["place name"];
              this.need.state = place["state abbreviation"];
              this.zipLoadingMessage =
                "✓ Zip code validated, city and state auto-filled";

              // Clear validation errors
              this.zipMessage = null;
              this.cityMessage = null;
              this.stateMessage = null;
            } else {
              this.zipLoadingMessage = "✓ Zip code validated";
              this.zipMessage = null;
            }
          }
        } else {
          this.zipMessage = "Invalid zip code";
          this.zipLoadingMessage = "";
        }
      } catch (error) {
        console.error("Zip code validation error:", error);
        this.zipMessage = "Unable to validate zip code";
        this.zipLoadingMessage = "";
      } finally {
        this.zipLoading = false;
        setTimeout(() => {
          this.zipLoadingMessage = "";
        }, 3000);
      }
    },
    validateStep(step) {
      let isValid = true;

      switch (step) {
        case 1:
          isValid =
            this.validateField("title") &&
            this.validateField("status") &&
            this.validateField("privacy");
          if (
            isValid &&
            (!this.need.title || /[^a-zA-Z\s]/.test(this.need.title))
          ) {
            this.titleMessage =
              "Need title can only contain letters and spaces";
            this.messageType = "error";
            isValid = false;
          }
          break;

        case 2:
          isValid = this.validateField("duration");
          if (this.need.duration !== "Is Ongoing") {
            isValid = isValid && this.validateField("start_date");
          
            if (this.need.duration === "Custom Shifts") {
          // Validate custom shifts
          isValid = isValid && this.validateField("custom_shifts");
        }
          
            if (this.need.duration === "Recurring Shifts") {
              isValid = isValid && this.validateField("occurs");
              if (this.need.occurs === "Daily") {
                isValid = isValid && this.validateField("repeat_every");
              } else if (this.need.occurs === "Weekly") {
                isValid = isValid && this.validateField("repeat_on");
              } else if (this.need.occurs === "Monthly") {
                isValid = isValid && this.validateField("dow");
                isValid = isValid && this.validateField("occurrence");
              }
            }
            if (this.need.duration !== "Custom Shifts") {
              isValid = isValid && this.validateField("hours");
              isValid = isValid && this.validateField("hours_description");
            }
            if (this.need.duration !== "Happens On") {
              isValid = isValid && this.validateField("end_date");
            }
            isValid = isValid && this.validateField("capacity");
          }
          break;

        case 3:
          isValid =
            this.validateField("min_age") && this.validateField("max_age");
          break;

        case 4:
          isValid =
            this.validateField("address") &&
            this.validateField("city") &&
            this.validateField("state") &&
            this.validateField("zip") &&
            this.validateField("interests");
          break;
      }

      if (isValid) {
        this.currentStep = step + 1;
      } else {
        this.scrollToFirstError();
      }

      return isValid;
    },
    scrollToFirstError() {
      this.$nextTick(() => {
        const firstError = document.querySelector(".v-messages.error--text");
        if (firstError) {
          firstError.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        }
      });
    },
  clearErrorMessages() {
    Object.keys(this.validationRules).forEach((field) => {
      this[`${field}Message`] = null;
    });
    this.customShiftsMessage = null; // Add this line
    this.error = null;
  },
    onChange() {
      this.clearErrorMessages();
    },
    // Clean the entire "need" object before saving
    cleanNeed(need) {
      return {
        ...need,
        title: this.sanitizeInput(need.title),
        description: this.sanitizeInput(need.description),
        hours: this.sanitizeInput(need.hours),
        hours_description: this.sanitizeInput(need.hours_description),
        address: this.sanitizeInput(need.address),
        city: this.sanitizeInput(need.city),
        state: this.sanitizeInput(need.state),
        zip: this.sanitizeInput(need.zip),
        attributes: (need.attributes || []).map((attr) =>
          this.sanitizeInput(attr)
        ),
        interests: (need.interests || []).map((i) => this.sanitizeInput(i)),
      };
    },
    async onSave() {
      // Validate all steps
      const step1Valid = this.validateStep(1);
      const step2Valid = this.validateStep(2);
      const step3Valid = this.validateStep(3);
      const step4Valid = this.validateStep(4);

      if (!step1Valid || !step2Valid || !step3Valid || !step4Valid) {
        // Go to the first step with errors
        if (!step1Valid) this.currentStep = 1;
        else if (!step2Valid) this.currentStep = 2;
        else if (!step3Valid) this.currentStep = 3;
        else this.currentStep = 4;
        return;
      }

      const safeNeed = this.cleanNeed(this.need);

      this.loading = true;
      try {
        await this.createNeed(safeNeed);
        this.loading = false;
        this.onClose();
        this.showSnackbar("Need created successfully!", "success", "mdi-check");
      } catch (error) {
        this.loading = false;
        this.error = error.response?.data?.message || "An error occurred";
        this.scrollToFirstError();
      }
    },
  },
  computed: {
    ...mapState("icon", {
      interests: "interestNames",
    }),
    isStep1Valid() {
      return !!this.need.status && !!this.need.privacy && !!this.need.title;
    },
    isStep2Valid() {
      if (!this.need.duration) return false;

      if (this.need.duration === "Is Ongoing") return true;

      if (this.need.duration === "Custom Shifts") {
        // For Custom Shifts, require at least one shift and need.start_date and need.end_date
        if (
          !this.need.custom_shifts ||
          !Array.isArray(this.need.custom_shifts) ||
          this.need.custom_shifts.length === 0
        ) {
           return false;
        }
        if (!this.need.start_date || !this.need.end_date) {
          return false;
        }
        return true;
      }

      if (!this.need.start_date) return false;

      if (this.need.duration === "Recurring Shifts") {
        if (!this.need.occurs) return false;
        if (this.need.occurs === "Daily" && !this.need.repeat_every)
          return false;
        if (
          this.need.occurs === "Weekly" &&
          (!this.need.repeat_on || !this.need.repeat_on.length)
        )
          return false;
        if (
          this.need.occurs === "Monthly" &&
          (!this.need.dow || !this.need.occurrence)
        )
          return false;
      }

      if (
        this.need.duration !== "Custom Shifts" &&
        this.need.duration !== "Is Ongoing"
      ) {
        if (!this.need.hours) return false;
        if (!this.need.hours_description) return false;
      }

      if (this.need.duration !== "Happens On" && !this.need.end_date)
        return false;

      if (
        this.need.duration !== "Is Ongoing" &&
        !this.need.capacity &&
        this.need.capacity !== 0
      )
        return false;

      // Check date order if both dates exist
      if (
        this.need.start_date &&
        this.need.end_date &&
        new Date(this.need.start_date) > new Date(this.need.end_date)
      ) {
        return false;
      }

      return true;
    },
    isStep3Valid() {
      if (
        this.need.min_age === null ||
        this.need.min_age === "" ||
        this.need.max_age === null ||
        this.need.max_age === ""
      ) {
        return false;
      }

      const minAge = Number(this.need.min_age);
      const maxAge = Number(this.need.max_age);

      if (isNaN(minAge) || isNaN(maxAge)) return false;
      if (minAge < 1 || minAge > 120) return false;
      if (maxAge < 1 || maxAge > 120) return false;
      if (minAge > maxAge) return false;

      return true;
    },
    isStep4Valid() {
      // Add debugging to understand what's happening
      const hasAddress = !!this.need.address;
      const hasCity = !!this.need.city;
      const hasState = !!this.need.state;

      // Use the same validation logic as validationRules.zip
      const hasZip = !!this.need.zip;
      let zipValid = false;
      if (hasZip) {
        const cleanZip = this.need.zip.replace(/\s/g, "");
        zipValid = /^\d{5}(-\d{4})?$/.test(cleanZip);
      }

      const hasInterests = !!(
        this.need.interests && this.need.interests.length
      );

      return (
        hasAddress && hasCity && hasState && hasZip && zipValid && hasInterests
      );
    },
    formProgress() {
      const totalFields = 15;
      const completedFields = [
        this.need.status,
        this.need.title,
        this.need.privacy,
        this.need.duration,
        this.need.min_age !== null,
        this.need.max_age !== null,
        this.need.address,
        this.need.city,
        this.need.state,
        this.need.zip,
        this.need.interests?.length > 0,
      ].filter(Boolean).length;
      return (completedFields / totalFields) * 100;
    },
  },
  //   watch: {
  //   // Force reactivity for step 4 validation
  //   'need.address': function(newVal) {
  //     console.log('Address changed:', newVal);
  //     this.$nextTick(() => this.$forceUpdate());
  //   },
  //   'need.city': function(newVal) {
  //     console.log('City changed:', newVal);
  //     this.$nextTick(() => this.$forceUpdate());
  //   },
  //   'need.state': function(newVal) {
  //     console.log('State changed:', newVal);
  //     this.$nextTick(() => this.$forceUpdate());
  //   },
  //   'need.zip': function(newVal) {
  //     console.log('Zip changed:', newVal);
  //     this.$nextTick(() => this.$forceUpdate());
  //   },
  //   'need.interests': {
  //     handler(newVal) {
  //       console.log('Interests changed:', newVal);
  //       this.$nextTick(() => this.$forceUpdate());
  //     },
  //     deep: true
  //   }
  // },
  mounted() {
    this.getInterestIcons().catch((error) => console.log(error));
  },
};
</script>

<style scoped>
.form-container {
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
}

.headline {
  font-size: 1.5rem;
  font-weight: 600;
  color: #fff;
}

.form-header {
  background: linear-gradient(135deg, #084468, #0a5b8a);
  padding: 20px 24px;
  border-bottom: 1px solid #e0e0e0;
}

.form-content {
  padding: 0;
  max-height: 70vh;
  overflow-y: auto;
}

.step-header {
  display: flex;
  align-items: center;
  font-weight: 500;
}

.next-btn,
.submit-btn {
  font-weight: 600;
  letter-spacing: 0.5px;
  text-transform: none;
  box-shadow: 0 3px 5px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
}

.next-btn:hover,
.submit-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 5px 10px rgba(0, 0, 0, 0.15);
}

.back-btn {
  text-transform: none;
  color: #666;
}

.close-btn {
  position: absolute;
  right: 16px;
  top: 16px;
}

.interest-chip {
  margin: 4px;
  transition: all 0.2s ease;
}

.interest-chip:hover {
  transform: scale(1.05);
}

.interest-hint {
  display: flex;
  align-items: center;
  font-size: 0.8rem;
  color: #666;
  margin-top: 4px;
}

/* Animation for form sections */
.slide-fade-enter-active {
  transition: all 0.3s ease;
}
.slide-fade-leave-active {
  transition: all 0.3s cubic-bezier(1, 0.5, 0.8, 1);
}
.slide-fade-enter,
.slide-fade-leave-to {
  transform: translateX(10px);
  opacity: 0;
}

/* Custom scrollbar */
.form-content::-webkit-scrollbar {
  width: 8px;
}
.form-content::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}
.form-content::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 4px;
}
.form-content::-webkit-scrollbar-thumb:hover {
  background: #555;
}

@media (max-width: 960px) {
  .form-container {
    margin: 0;
    height: 100vh;
    max-height: 100vh;
  }
  .form-content {
    max-height: calc(100vh - 120px);
  }
}

.error--text {
  color: #ff5252 !important;
  caret-color: #ff5252 !important;
}
.error--text .v-input__slot {
  border-color: #ff5252 !important;
}
.error--text .v-label {
  color: #ff5252 !important;
}
.error--text .v-messages__message {
  color: #ff5252 !important;
}

.v-messages__message {
  font-size: 0.75rem;
  margin-top: 4px;
}

.success--text .v-input__slot {
  border-color: #4caf50 !important;
}
</style>
